/* eslint-disable @typescript-eslint/no-unused-vars */
import {
	Breakpoints,
	BreakpointWrapper,
	Container,
	ContainerGutter,
	ContainerPadding,
	CTA,
	FocusLayer,
	LayerWrapper,
	Layout,
	Richtext,
	ShimLayer,
	Text,
	TextAlignment,
	TokenTextAppearance,
	TokenTextColor,
} from '@volkswagen-onehub/components-core';
import * as Icons from '@volkswagen-onehub/icons-core';
import { Close } from '@volkswagen-onehub/icons-core';
import {
	InteractionLayerSizeV2,
	LayerHandleV2,
} from '@volkswagen-onehub/layer-manager';
import { motion, useMotionValueEvent, useScroll } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { CustomBreakpoints } from '../helpers/breakpoints';
import {
	getFinanciacionType,
	getPagoFinanciado,
	getPagoUnico,
} from '../helpers/ofertas-helper';
import {
	addCarlineInPath,
	cleanCarlineInPath,
	hasCarlineInPath,
} from '../helpers/ofertas-url-manager';
import {
	CarlineWithOfertas,
	FinanceData,
	Oferta,
	Promocion,
} from '../services/ofertas-service/models/ofertas';
import { useTrackingManager } from '../tracking/use-tracking-manager';
import { ContentLayer, Gradient } from './layer/content-layer';
import { OfertaLayer } from './layer/oferta-layer';
import { ModelFrame } from './model-frame';
import { OfertasCardContainer } from './ofertas-card-container';
import { DesktopSelector } from './selectors/desktop-selector';
import { MobileSelector } from './selectors/mobile-selector';

const OfertasSection = styled.section<{
	numberOfCarlines: number;
	hasOneCarline: boolean;
}>`
	width: 100%;
	height: ${(props) =>
		props.hasOneCarline ? '100vh' : `${props.numberOfCarlines * 100}vh`};
	position: relative;

	@media screen and (max-width: 1199px) {
		height: ${(props) => props.hasOneCarline && 'fit-content'};
		display: ${(props) => props.hasOneCarline && 'block'};
		position: ${(props) => props.hasOneCarline && 'unset'};
	}

	ul {
		list-style-type: none;
		padding: 0;
	}
`;

const StyledContainer = styled.div`
	overflow: hidden;

	& > div {
		display: flex;
		align-items: center;
	}
`;

const OfertasStickyContainer = styled.div<{ hasOneCarline: boolean }>`
	background: linear-gradient(180deg, #f6f6f6 0%, #dfe4e8 100%);
	position: sticky;
	top: ${(props) => props.theme.size.dynamic0150};
	height: 100vh;
	overflow: visible;

	@media screen and (max-width: 1199px) {
		height: ${(props) => props.hasOneCarline && 'fit-content'};
		display: ${(props) => props.hasOneCarline && 'block'};
		position: ${(props) => props.hasOneCarline && 'unset'};
		align-content: ${(props) => props.hasOneCarline && 'center'};
	}
`;

const RelativeContainer = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;

	position: relative;
`;

const OfertasMobileContainer = styled.div`
	overflow: visible !important;
`;

const ModelListContainer = styled.div`
	width: 40vw;
	left: 50px;
	position: relative;
	z-index: 2;
	pointer-events: none;

	@media screen and (max-width: 959px) {
		left: 0;
		width: 100vw;
		overflow: visible;
		pointer-events: auto;
	}

	@media screen and (min-width: 960px) and (max-width: 1199px) {
		left: 0;
		width: 80vw;
		overflow: visible;
	}
`;

const ModelFrameContainer = styled.div<{ hasOneCarline: boolean }>`
	position: absolute;
	width: 100%;
	height: 100vh;
	top: 0;
	left: 0;

	@media screen and (max-width: 1199px) {
		position: ${(props) => props.hasOneCarline && 'unset'};
		height: ${(props) => props.hasOneCarline && 'fit-content'};
	}
`;

const ModelFrameList = styled.div<{ hasOneCarline: boolean }>`
	height: 100vh;
	overflow-x: hidden;
	overflow-y: hidden;

	@media screen and (min-width: 1200px) {
		display: flex;
		align-items: center;
		justify-content: space-evenly;
	}

	@media screen and (max-width: 1199px) {
		height: ${(props) => props.hasOneCarline && 'fit-content'};
	}
`;

const FadeContainer = styled.div<{ numberOfModels: number }>`
	display: flex;
	align-items: center;
	pointer-events: none;

	@media screen and (min-width: 960px) {
		height: 30vh;
		margin-bottom: 30px;
		padding-top: ${(props) => props.numberOfModels > 7 && '60px'};

		overflow: hidden;
		-webkit-mask: linear-gradient(
			0deg,
			transparent,
			white 10%,
			white 90%,
			transparent
		);
		mask: linear-gradient(0deg, transparent, white 10%, white 90%, transparent);
	}

	@media screen and (min-width: 1200px) {
		height: 65vh;
		padding-top: ${(props) => props.numberOfModels > 7 && '120px'};
		padding-bottom: 30px;
	}

	@media screen and (min-width: 1600px) {
		height: 80vh;
		padding-top: 0;
	}
`;

const ModelsList = styled.ul`
	transition: margin-top 1s ease; // Define la animación para marginTop
	pointer-events: auto;

	@media screen and (max-width: 959px) {
		display: none;
	}

	@media screen and (min-width: 960px) and (max-width: 1199px) {
		max-height: 25vh;
	}

	@media screen and (min-width: 1200px) and (max-width: 1599px) {
		max-height: 55vh;
	}

	@media screen and (min-width: 1600px) {
		max-height: 65vh;
	}
`;

const Model = styled.li`
	padding-bottom: ${(props) => props.theme.size.static150};
`;

const WhiteBackgroundContainer = styled.div`
	pointer-events: auto;
	position: relative;
	z-index: 10;
`;

const FixedDiv = styled.div`
	position: fixed;
	bottom: 24px;
	left: 50%;
	transform: translateX(-50%);
	background: #ffffff;
	border-radius: 15px;
	z-index: 2;
`;

const slideRight = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(10px);
  }
`;

const AnimationContainer = styled.div<{ isActive: boolean }>`
	display: inline-block;
	animation: ${({ isActive }) => (isActive ? slideRight : 'none')} 0.5s forwards;
`;

const TrackingContainer = styled.div`
	pointer-events: auto;
	cursor: pointer !important;
`;

export const Ofertas: React.FC<{
	carlines: CarlineWithOfertas[];
	hasOneCarline: boolean;
	handleFilteredCarlines: (selectedFilter: string) => void;
	selectedGamaFilter: string | null;
	selectedMotorizacionFilter: string | null;
	setSelectedGamaFilter: React.Dispatch<React.SetStateAction<string | null>>;
	setSelectedMotorizacionFilter: React.Dispatch<
		React.SetStateAction<string | null>
	>;
	isCallMeNowOpen: boolean;
}> = ({
	carlines,
	hasOneCarline,
	handleFilteredCarlines,
	selectedGamaFilter,
	selectedMotorizacionFilter,
	setSelectedGamaFilter,
	setSelectedMotorizacionFilter,
	isCallMeNowOpen,
}) => {
	/* Inicializamos sin carlines a la espera de actualización */
	const [activeCarlineIndex, setActiveCarlineIndex] = useState<number>(-1);
	const [numberOfCarlines, setNumberOfCarlines] = useState<number>(0);

	//Gestion de carlines en la url
	const carlineSelectedURL = React.useRef<boolean>(hasCarlineInPath(carlines));
	const disableScrollDetection = React.useRef<boolean>(false);

	const [selectedOferta, setSelectedOferta] = useState<Oferta>(null);
	const [selectedPromotion, setSelectedPromotion] = useState<
		Promocion | undefined
	>(undefined);

	//		VARIABLES FUERA DE USO YA QUE LA CALCULADORA ESTA DESACTIVADA
	/*Variables necesarias para abrir el layer de financiacion*/
	const financeLayerRef = React.useRef<LayerHandleV2<unknown>>(null);
	const [isFinanceLayerVisible, setIsFinanceLayerVisible] =
		React.useState<boolean>(false);
	const [financeData, setFinanceData] = useState<FinanceData>({
		request: undefined,
		selectedOferta: undefined,
	});

	/*Iniciamos state de control de layer */
	const [isLayerVisible, setIsLayerVisible] = useState(false);
	const [isLayerActive, setIsLayerActive] = useState<boolean>(false);
	const [interactionLayerSize, setInteractionLayerSize] =
		useState<InteractionLayerSizeV2>(InteractionLayerSizeV2.B);

	/* useRef para controlar sobre que carlines he ejecutado el evento VWBasic_Vehicle_Load */
	const carlineBasicVehicleLoad = useRef(new Set());

	const trackingManager = useTrackingManager();

	/* Variables y funciones auxiliares para scroll controlado */
	let currentScrollDirection = 'down';
	const isAutoScrollingRef = useRef(false);

	function scrollToCarline(scroll: number) {
		isAutoScrollingRef.current = true;

		window.scrollTo({
			top: scroll,
		});
		// Utiliza un pequeño retraso para asegurarte de que el scroll se complete
		setTimeout(() => {
			isAutoScrollingRef.current = false;
		}, 0); // El retraso de 0 asegura que el código espere a que el evento de scroll se procese
	}
	/* Fin variables y funciones auxiliares para scroll controlado */

	const handleLayerOnClick = () => {
		setIsLayerActive(!isLayerActive);
	};

	const handleScrollToDisclaimer = (reference: string) => {
		//Activamos el autoscroll para que no haya problemas
		isAutoScrollingRef.current = true;

		//Guardamos la posición inicial
		const initialScrollPosition =
			window.pageYOffset || document.documentElement.scrollTop;

		//Obtenemos todos los legales (se localizan por usar dt)
		const allDts = document.querySelectorAll('dt');
		for (const dt of Array.from(allDts)) {
			//Limpiamos el . que tienen el numero del legal (2.)
			if (dt.textContent?.trim() === `${reference}.`) {
				//Cogemos el legal de la lista que tiene dicha referencia (2)
				const li = dt.closest('li');
				if (li) {
					//Ahora cogemos el mensaje del legal para añadirle el (volver arriba)
					const dd = li.querySelector('dd');
					if (dd && !dd.querySelector('.back-to-top')) {
						const span = document.createElement('span');
						span.className = 'back-to-top';
						span.style.cssText =
							'display:block;cursor:pointer;color:inherit;text-decoration:underline;font-weight:bold;';
						span.textContent = '↑ Volver arriba';
						span.onclick = () => {
							//Activamos el auto scroll para volver al legal y desactiamos el autoscroll al pasar 1500ms
							isAutoScrollingRef.current = true;
							window.scrollTo({
								top: initialScrollPosition,
								behavior: 'smooth',
							});
							setTimeout(() => {
								isAutoScrollingRef.current = false;
							}, 1500);
							span.remove();
						};
						dd.appendChild(span);
					}

					//Obtenemos la posicion del legal en la lista
					const rect = li.getBoundingClientRect();
					//Obtenemos cuanto se ha scrolleado ya para tenerlo en cuenta
					const scrollTop =
						window.pageYOffset || document.documentElement.scrollTop;
					//Hacemos scroll automatico con 100 px de margen
					window.scrollTo({
						top: scrollTop + rect.top - 100,
						behavior: 'smooth',
					});
					//Hacemos focus en el legal
					setTimeout(() => {
						li.setAttribute('tabindex', '-1');
						li.focus();
					}, 800);

					//desactivamos el autoscroll
					setTimeout(() => {
						isAutoScrollingRef.current = false;
					}, 1000);

					return;
				}
			}
		}
		isAutoScrollingRef.current = false;
	};

	/* Inicializamos referencias */
	const sectionRefs: { [key: string]: React.RefObject<HTMLDivElement> } = {};
	// carlines.forEach((carline) => {
	//  sectionRefs[carline.name] = useRef(null);
	// });

	const ofertasRef = useRef<HTMLDivElement | null>(null);

	/* Variables y funciones para el control del scroll */
	const scrollContainer = document.getElementById('scroll-container');

	const lastModelElement = document.getElementById(
		`model-${numberOfCarlines - 1}`,
	);

	const modelElementHeight = Number(
		lastModelElement &&
			lastModelElement?.getBoundingClientRect().height +
				lastModelElement?.style.marginTop,
	);

	const modelListAutoScroll = () => {
		//Hacemos scroll hasta el carline
		if (activeCarlineIndex >= 0 && scrollContainer) {
			const visibleElements = Math.floor(
				scrollContainer?.getBoundingClientRect().height / modelElementHeight,
			);

			if (visibleElements && scrollContainer) {
				if (visibleElements < numberOfCarlines) {
					if (activeCarlineIndex > visibleElements / 2) {
						const marginTop =
							-1 *
							modelElementHeight *
							(activeCarlineIndex - visibleElements / 2);
						scrollContainer.style.marginTop = `${marginTop}px`;
					} else {
						scrollContainer.style.marginTop = `${modelElementHeight * 2}px`;
					}
				} else {
					scrollContainer.style.marginTop = `${modelElementHeight * 2}px`;
				}
			}
		}
	};
	/* Fin variables y funciones para el control del scroll */

	//useEffect que se ejecuta cada vez que se cambia de coche
	useEffect(() => {
		if (activeCarlineIndex >= 0) {
			//Recalculamos margins de modelList
			modelListAutoScroll();

			if (carlineSelectedURL.current) {
				addCarlineInPath(activeCarlineIndex, carlines);
				carlineSelectedURL.current = false;
			} else if (!disableScrollDetection.current) {
				cleanCarlineInPath();
			}

			//Scorll hasta carline seleccioando
			const viewportHeight = window.innerHeight;
			const rect = ofertasRef.current.getBoundingClientRect();
			const scrollY = window.scrollY; // Obtiene el desplazamiento actual
			const ofertasRefTop = rect.top + scrollY;

			let scrollTo = ofertasRefTop;
			if (numberOfCarlines > 1) {
				const isLastCarline = activeCarlineIndex === numberOfCarlines - 1;
				scrollTo = isLastCarline
					? ofertasRefTop + viewportHeight * activeCarlineIndex
					: ofertasRefTop + viewportHeight * (activeCarlineIndex + 0.5);
			}

			// Validamos si el scrollYProgress concuerda con el activeCarlineIndex, en caso que no lo corregimos
			if (
				(1 / numberOfCarlines) * activeCarlineIndex < scrollYProgress.current &&
				(1 / numberOfCarlines) * (activeCarlineIndex + 1) >
					scrollYProgress.current
			) {
				return;
			} else {
				scrollToCarline(scrollTo);
			}
		}
	}, [activeCarlineIndex, modelListAutoScroll, numberOfCarlines]);

	const containerVariants = {
		visible: {
			transition: {
				staggerChildren: 0.2,
			},
		},
	};

	const variants = {
		hidden: { opacity: 0, y: currentScrollDirection === 'up' ? 50 : -50 },
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				duration: 0.5,
				ease: 'easeInOut',
			},
		},
	};

	/* Control de scroll */
	const { scrollYProgress } = useScroll({
		target: ofertasRef,
		offset: ['start start', 'end end'],
	});

	useMotionValueEvent(scrollYProgress, 'change', (value) => {
		if (
			!isAutoScrollingRef.current &&
			!disableScrollDetection.current &&
			numberOfCarlines > 0
		) {
			const carlineIndex = Math.min(
				Math.floor(value * numberOfCarlines),
				numberOfCarlines - 1,
			);

			if (carlineIndex !== activeCarlineIndex && carlineIndex >= 0) {
				const scrollDirection =
					carlineIndex < activeCarlineIndex ? 'up' : 'down';
				currentScrollDirection = scrollDirection;
				setActiveCarlineIndex(carlineIndex);
				setSelectedOferta(carlines[carlineIndex].ofertas[0]);

				if (!carlineBasicVehicleLoad.current.has(carlines[carlineIndex].code)) {
					const pagoFinanciado = getPagoFinanciado(
						carlines[carlineIndex].ofertas[0].datosEconomicos,
					);
					trackingManager.trackOfertasVehicleLoad({
						CarlineId: carlines[carlineIndex].code.toString(),
						CarlineName: carlines[carlineIndex].title,
						SalesgroupName:
							carlines[carlineIndex].ofertas[0].carline?.salesgroup.title,
						ModelId:
							carlines[
								carlineIndex
							].ofertas[0].configuracion.motorizacion.code?.split('-')[0],
						ModelName:
							carlines[carlineIndex].ofertas[0].carline?.salesgroup.model.name,
						ModelYear:
							carlines[
								carlineIndex
							].ofertas[0].carline?.salesgroup.year.toString(),
						PriceRateType: pagoFinanciado
							? getFinanciacionType(pagoFinanciado)
							: undefined,
						PriceRateTotal: pagoFinanciado?.precio ?? undefined,
						PriceRateCurrency: 'EUR',
						PriceTotalTotal:
							getPagoUnico(carlines[carlineIndex].ofertas[0].datosEconomicos)
								?.precio ?? undefined,
						PriceTotalCurrency: 'EUR',
					});
					carlineBasicVehicleLoad.current.add(carlines[carlineIndex].code);
				}
			}
		}
	});
	/* Fin control de scroll */

	const formatPromotionText = (promotionText: string) => {
		const formatedPromotionText = promotionText.replace(/\n/g, '\n<br>\n');

		return formatedPromotionText;
	};

	const handleTrackingOnClick = (
		carline: CarlineWithOfertas,
		index: number,
	) => {
		carlineSelectedURL.current = true;

		setActiveCarlineIndex(index);
		setSelectedOferta(carline.ofertas[0]);

		const pagoFinanciado = getPagoFinanciado(
			carline.ofertas[0].datosEconomicos,
		);

		trackingManager.trackOfertasModelSelectClick({
			CarlineId: carline.code.toString(),
			CarlineName: carline.title,
			SalesgroupName: carline.ofertas[0].carline?.salesgroup.title,
			ModelId:
				carline.ofertas[0].configuracion.motorizacion.code?.split('-')[0],
			ModelName: carline.ofertas[0].carline?.salesgroup.model.name,
			ModelYear: carline.ofertas[0].carline?.salesgroup.year.toString(),
			PriceRateType: pagoFinanciado
				? getFinanciacionType(pagoFinanciado)
				: undefined,
			PriceRateTotal: pagoFinanciado?.precio ?? undefined,
			PriceRateCurrency: 'EUR',
			PriceTotalTotal:
				getPagoUnico(carline.ofertas[0].datosEconomicos)?.precio ?? undefined,
			PriceTotalCurrency: 'EUR',
		});

		trackingManager.trackOfertasVehiclePageload({
			CarlineId: carline.code.toString(),
			CarlineName: carline.title,
			SalesgroupName: carline.ofertas[0].carline?.salesgroup.title,
			ModelId:
				carline.ofertas[0].configuracion.motorizacion.code?.split('-')[0],
			ModelName: carline.ofertas[0].carline?.salesgroup.model.name,
			ModelYear: carline.ofertas[0].carline?.salesgroup.year.toString(),
			PriceRateType: pagoFinanciado
				? getFinanciacionType(pagoFinanciado)
				: undefined,
			PriceRateTotal: pagoFinanciado?.precio ?? undefined,
			PriceRateCurrency: 'EUR',
			PriceTotalTotal:
				getPagoUnico(carline.ofertas[0].datosEconomicos)?.precio ?? undefined,
			PriceTotalCurrency: 'EUR',
		});
	};

	/* Reinicialización al cargar los carlines/CARGA INICIAL */
	useEffect(() => {
		if (carlines) {
			const prevNumberOfCarlines = numberOfCarlines;
			setNumberOfCarlines(carlines.length);

			const carlineUrlSplitted = window.location.pathname.split('/');
			const carlineUrl = carlineUrlSplitted[
				carlineUrlSplitted.length - 1
			].replace('.app', '');

			const carlineIndex = carlines
				.map(function (carline) {
					return carline.name;
				})
				.indexOf(carlineUrl);

			if (carlineIndex >= 0) {
				setActiveCarlineIndex(carlineIndex);
				setSelectedOferta(carlines[carlineIndex].ofertas[0]);

				if (hasCarlineInPath(carlines)) {
					//Desactivamos la detección de scroll temporalmente
					disableScrollDetection.current = true;
					setTimeout(() => {
						disableScrollDetection.current = false;
					}, 500);
				}
			} else {
				cleanCarlineInPath();
				setActiveCarlineIndex(0);
				setSelectedOferta(carlines[0].ofertas[0]);

				if (
					prevNumberOfCarlines !== carlines.length &&
					window.innerWidth < 960
				) {
					setTimeout(() => {
						const rect = ofertasRef.current?.getBoundingClientRect();
						if (rect) {
							window.scrollTo({ top: rect.top + window.scrollY - 50 });
						}
					}, 100);
				}
			}
		}
	}, [carlines]);

	useEffect(() => {
		const checkScreenSize = () => {
			if (window.innerWidth < 1200) {
				setInteractionLayerSize(InteractionLayerSizeV2.D);
			}

			if (window.innerWidth > 1199) {
				setInteractionLayerSize(InteractionLayerSizeV2.B);
			}
		};

		checkScreenSize();
		window.addEventListener('resize', checkScreenSize);

		return () => window.removeEventListener('resize', checkScreenSize);
	}, []);

	if (!carlines || carlines.length === 0) {
		return null;
	}

	return (
		<>
			{/* CALCULADORA DESACTIVADA, SI SE RETOMA SE DEBE FIXEAR */}
			{/* <FinanceLayer
				financeData={financeData}
				setIsFinanceLayerVisible={setIsFinanceLayerVisible}
				isFinanceLayerVisible={isFinanceLayerVisible}
				financeLayerRef={financeLayerRef}
				setIsLayerVisible={setIsLayerVisible}
			/> */}
			<OfertasSection
				className="ofertas-section"
				numberOfCarlines={numberOfCarlines}
				hasOneCarline={hasOneCarline}
				ref={ofertasRef}
			>
				<ContentLayer
					className="content-layer"
					isLayerVisible={isLayerVisible}
					gradientType={Gradient.LINEAR_GRADIENT_RIGHT}
					layerSize={interactionLayerSize}
				>
					<OfertaLayer
						className="oferta-layer"
						setIsLayerVisible={setIsLayerVisible}
						selectedOferta={selectedOferta}
						activeCarline={carlines[activeCarlineIndex]}
						setIsFinanceLayerVisible={setIsFinanceLayerVisible}
						setFinanceData={setFinanceData}
						isCallMeNowOpen={isCallMeNowOpen}
					/>
				</ContentLayer>
				{activeCarlineIndex >= 0 && (
					<OfertasStickyContainer
						className="ofertas__sticky-container"
						hasOneCarline={hasOneCarline}
					>
						{/* OFERTAS VERSION TABLET Y DESKTOP */}
						<BreakpointWrapper min={Breakpoints.b960}>
							<Layout
								data-testid="layout-ofertas-desktop"
								appearance={{
									[Breakpoints.default]: [{ name: 'a', columns: 24 }],
									[Breakpoints.b960]: hasOneCarline
										? [
												{ name: '.', columns: 0 },
												{ name: 'a', columns: 24 },
												{ name: '.', columns: 0 },
											]
										: [
												{ name: '.', columns: 1 },
												{ name: 'a', columns: 22 },
												{ name: '.', columns: 1 },
											],
								}}
							>
								{!hasOneCarline && (
									<WhiteBackgroundContainer className="white-background-container">
										<DesktopSelector
											handleFilteredCarlines={handleFilteredCarlines}
											selectedGamaFilter={selectedGamaFilter}
											selectedMotorizacionFilter={selectedMotorizacionFilter}
											setSelectedGamaFilter={setSelectedGamaFilter}
											setSelectedMotorizacionFilter={
												setSelectedMotorizacionFilter
											}
										/>
									</WhiteBackgroundContainer>
								)}

								{/* LISTA DE CARLINES */}
								<ModelListContainer
									className="model-list-container main-container"
									id="main-container"
								>
									<RelativeContainer
										className="relative-container"
										id="relative-container"
									>
										{!hasOneCarline && (
											<FadeContainer
												className="fade-container"
												id="fade-container"
												numberOfModels={carlines.length}
											>
												<ModelsList
													className="models-list scroll-container"
													id="scroll-container"
												>
													{carlines.map((carline, index) => {
														const isActive = activeCarlineIndex === index;

														return (
															<Model
																key={index}
																className={`model-${carline.name}`}
																id={`model-${index}`}
															>
																<TrackingContainer
																	className="tracking-div"
																	onClick={() =>
																		handleTrackingOnClick(carline, index)
																	}
																>
																	<Container
																		wrap={'never'}
																		gutter={ContainerGutter.static100}
																		verticalAlign={'center'}
																	>
																		{isActive && (
																			<Text>
																				<AnimationContainer isActive={isActive}>
																					<Icons.ArrowRight variant="small" />
																				</AnimationContainer>
																			</Text>
																		)}

																		<Text
																			appearance={
																				TokenTextAppearance.headline200
																			}
																			color={
																				isActive
																					? TokenTextColor.primary
																					: TokenTextColor.secondary
																			}
																			bold
																		>
																			<AnimationContainer isActive={isActive}>
																				{carline.nuevo ? 'NUEVO ' : ''}
																				{carline.title.toLocaleUpperCase()}
																			</AnimationContainer>
																		</Text>
																	</Container>
																</TrackingContainer>
															</Model>
														);
													})}
												</ModelsList>
											</FadeContainer>
										)}
									</RelativeContainer>
								</ModelListContainer>

								{/* CARD DE LA OFERTA Y CARROUSEL DE IMAGENES */}
								<ModelFrameContainer
									className="model-frame-container"
									hasOneCarline={hasOneCarline}
								>
									<ModelFrameList
										className="model-frame-list"
										hasOneCarline={hasOneCarline}
									>
										<motion.div
											initial="hidden"
											animate="visible"
											variants={containerVariants}
										>
											{carlines.map((carline, index) => {
												const isActive = activeCarlineIndex === index;
												return (
													<motion.section
														key={index}
														ref={sectionRefs[carline.name]}
														id={carline.name}
														initial="hidden"
														animate={isActive ? 'visible' : 'hidden'}
														variants={variants}
													>
														<StyledContainer className="prova-styled">
															<ModelFrame
																className={`model-frame-${carline.name}`}
																carline={carlines[activeCarlineIndex]}
																hasOneCarline={hasOneCarline}
																isCallMeNowOpen={isCallMeNowOpen}
																isActive={isActive}
																isLayerVisible={isLayerVisible}
																setIsLayerVisible={setIsLayerVisible}
																selectedOferta={selectedOferta}
																setSelectedOferta={setSelectedOferta}
																handleLayerOnClick={handleLayerOnClick}
																setSelectedPromotion={setSelectedPromotion}
																setIsFinanceLayerVisible={
																	setIsFinanceLayerVisible
																}
																setFinanceData={setFinanceData}
																handleScrollToDisclaimer={
																	handleScrollToDisclaimer
																}
															/>
														</StyledContainer>
													</motion.section>
												);
											})}
										</motion.div>
									</ModelFrameList>
								</ModelFrameContainer>

								{/* CARD OFERTA EN VERSION TABLET */}
								<BreakpointWrapper
									min={Breakpoints.b960}
									max={CustomBreakpoints.DESKTOP}
								>
									<OfertasCardContainer
										className="ofertas-card-container-tablet"
										isMobile={false}
										carline={carlines[activeCarlineIndex]}
										hasOneCarline={hasOneCarline}
										isLayerVisible={isLayerVisible}
										setIsLayerVisible={setIsLayerVisible}
										selectedOferta={selectedOferta}
										setSelectedOferta={setSelectedOferta}
										handleLayerOnClick={handleLayerOnClick}
										setSelectedPromotion={setSelectedPromotion}
										setIsFinanceLayerVisible={setIsFinanceLayerVisible}
										setFinanceData={setFinanceData}
										handleScrollToDisclaimer={handleScrollToDisclaimer}
										isCallMeNowOpen={isCallMeNowOpen}
									/>
								</BreakpointWrapper>
							</Layout>
						</BreakpointWrapper>

						{/* OFERTAS VERSION MOBILE */}
						<BreakpointWrapper min={Breakpoints.default} max={Breakpoints.b960}>
							<OfertasMobileContainer data-testid="mobile-container">
								<Container
									data-testid="model-selector-container-Mobile"
									padding={{
										top: {
											[Breakpoints.default]: ContainerPadding.dynamic0300,
											[Breakpoints.b560]: ContainerPadding.dynamic0150,
										},
										left: ContainerPadding.grid001,
										right: ContainerPadding.grid001,
										bottom: ContainerPadding.grid001,
									}}
									horizontalAlign={'center'}
									stretchContent
									style={{
										pointerEvents: 'auto',
										position: 'relative',
										zIndex: 10,
										overflow: 'visible',
									}}
								>
									{!hasOneCarline && (
										<MobileSelector
											carlines={carlines}
											activeCarlineIndex={activeCarlineIndex}
											setActiveCarlineIndex={setActiveCarlineIndex}
											setSelectedOferta={setSelectedOferta}
											selectedGamaFilter={selectedGamaFilter}
											selectedMotorizacionFilter={selectedMotorizacionFilter}
											setSelectedGamaFilter={setSelectedGamaFilter}
											setSelectedMotorizacionFilter={
												setSelectedMotorizacionFilter
											}
											carlineSelectedURL={carlineSelectedURL}
										/>
									)}
								</Container>

								<ModelFrameContainer
									className="model-frame-container"
									hasOneCarline={hasOneCarline}
								>
									<ModelFrameList
										className="model-frame-list"
										hasOneCarline={hasOneCarline}
									>
										{activeCarlineIndex >= 0 &&
											carlines[activeCarlineIndex] && (
												<motion.section
													key={activeCarlineIndex}
													id={carlines[activeCarlineIndex].name}
													initial="hidden"
													animate="visible"
													variants={variants}
												>
													<StyledContainer className="prova-styled">
														<OfertasCardContainer
															className="ofertas-card-container-mobile"
															carline={carlines[activeCarlineIndex]}
															hasOneCarline={hasOneCarline}
															isMobile={true}
															isLayerVisible={isLayerVisible}
															setIsLayerVisible={setIsLayerVisible}
															selectedOferta={selectedOferta}
															setSelectedOferta={setSelectedOferta}
															handleLayerOnClick={handleLayerOnClick}
															setSelectedPromotion={setSelectedPromotion}
															setIsFinanceLayerVisible={
																setIsFinanceLayerVisible
															}
															setFinanceData={setFinanceData}
															handleScrollToDisclaimer={
																handleScrollToDisclaimer
															}
															isCallMeNowOpen={isCallMeNowOpen}
														/>
													</StyledContainer>
												</motion.section>
											)}
									</ModelFrameList>
								</ModelFrameContainer>
							</OfertasMobileContainer>
						</BreakpointWrapper>
					</OfertasStickyContainer>
				)}
			</OfertasSection>
			{isLayerActive && selectedPromotion?.descripcion && (
				<LayerWrapper contentCentered>
					<>
						<ShimLayer />
						<FocusLayer active size="B" aria-label="promotion-layer">
							<Container
								verticalAlign={'flex-end'}
								horizontalAlign={'flex-end'}
								padding={{
									top: {
										[Breakpoints.default]: ContainerPadding.dynamic0100,
										[Breakpoints.b1600]: ContainerPadding.dynamic0100,
									},
									right: ContainerPadding.dynamic0100,
								}}
								stretchContent
							>
								<Text textAlign={TextAlignment.end}>
									<CTA
										tag="button"
										emphasis="secondary"
										size="small"
										onClick={handleLayerOnClick}
										icon={<Icons.Close variant="small" />}
									/>
								</Text>
							</Container>
							<Container
								wrap="always"
								padding={{
									top: ContainerPadding.static200,
									bottom: {
										[Breakpoints.default]: ContainerPadding.static500,
										[Breakpoints.b560]: ContainerPadding.static200,
									},
								}}
							>
								<Layout
									data-testid="layout-label"
									appearance={{
										[Breakpoints.default]: [
											{ name: '.', columns: 2 },
											{ name: 'a', columns: 20 },
											{ name: '.', columns: 2 },
										],
										[Breakpoints.b560]: [
											{ name: '.', columns: 1 },
											{ name: 'a', columns: 22 },
											{ name: '.', columns: 1 },
										],
										[Breakpoints.b960]: [
											{ name: '.', columns: 2 },
											{ name: 'a', columns: 20 },
											{ name: '.', columns: 2 },
										],
									}}
								>
									<Richtext
										markdown={formatPromotionText(
											selectedPromotion?.descripcion,
										)}
										dangerouslyParseRawHTML
									/>
									<Container
										padding={{
											top: ContainerPadding.static400,
											bottom: ContainerPadding.dynamic0100,
										}}
										verticalAlign={'center'}
										stretchContent
									>
										{selectedPromotion.cta.url && (
											<CTA
												tag="a"
												emphasis="secondary"
												href={selectedPromotion.cta.url}
											>
												{selectedPromotion.cta.label}
											</CTA>
										)}
									</Container>
								</Layout>
							</Container>
						</FocusLayer>
						<BreakpointWrapper min={Breakpoints.default} max={Breakpoints.b560}>
							<FixedDiv className="fixed-div">
								<CTA
									tag="button"
									emphasis="secondary"
									size="small"
									onClick={handleLayerOnClick}
									icon={<Close variant="small" />}
								>
									Cerrar
								</CTA>
							</FixedDiv>
						</BreakpointWrapper>
					</>
				</LayerWrapper>
			)}
		</>
	);
};
